import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiChevronDown, FiDownload, FiEye, FiEyeOff, FiKey, FiRefreshCw, FiSearch, FiShield, FiTrash2, FiX } from 'react-icons/fi';

import IconWrapper from '../../IconWrapper';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { useAuth } from '../../../contexts/AuthContext';
import { downloadFileWithAuth, fetchWithAuth, isAdminUser } from '../../../utils/api';

interface SkillSecretStatus {
  key: string;
  label: string;
  description: string;
  required: boolean;
  sensitive: boolean;
  scopes: string[];
  user_value_set: boolean;
  organization_value_set: boolean;
  environment_value_set: boolean;
  effective_source: 'session' | 'user' | 'organization' | 'environment' | 'unset';
}

interface SkillSummary {
  skill_name: string;
  name: string;
  description: string;
  version: string;
  skill_config: Record<string, unknown>;
  secret_statuses: SkillSecretStatus[];
}

interface SkillFile {
  relative_path: string;
  category: string;
  viewable: boolean;
}

interface SkillDetail extends SkillSummary {
  instructions: string;
  tools: string[];
  scripts: string[];
  references: string[];
  files: SkillFile[];
}

interface SkillFileContentResponse {
  skill: string;
  relative_path: string;
  content: string;
}

type SecretScope = 'user' | 'organization';

const sourceBadgeStyles: Record<SkillSecretStatus['effective_source'], string> = {
  session: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  user: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  organization: 'border-blue-200 bg-blue-50 text-blue-700',
  environment: 'border-amber-200 bg-amber-50 text-amber-700',
  unset: 'border-slate-200 bg-slate-50 text-slate-600',
};

const sourceLabels: Record<SkillSecretStatus['effective_source'], string> = {
  session: 'Session override',
  user: 'Personal override',
  organization: 'Organization default',
  environment: 'Environment fallback',
  unset: 'Unset',
};

const scopeCopy: Record<
  SecretScope,
  { title: string; action: string; description: string }
> = {
  user: {
    title: 'Personal override',
    action: 'Save personal value',
    description:
      'Used when YOU run the skill interactively (e.g. in chat). Scheduled monitors and automation do NOT see this — they always use the Organization default.',
  },
  organization: {
    title: 'Organization default',
    action: 'Save org default',
    description:
      'Used by scheduled monitors, imports, and any automation that runs on behalf of the organization. Set this for the Monitor wizard to work.',
  },
};

const buildSecretStateKey = (skillName: string, secretKey: string, scope: SecretScope) =>
  `${skillName}:${secretKey}:${scope}`;

const statCardClassName =
  'border-slate-200/80 bg-white/90 shadow-sm supports-[backdrop-filter]:bg-white/80';

const SkillsView: React.FC = () => {
  const { user } = useAuth();
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<SkillDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [selectedFilePath, setSelectedFilePath] = useState<string>('SKILL.md');
  const [selectedFileContent, setSelectedFileContent] = useState<string>('');
  const [fileLoading, setFileLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingStates, setSavingStates] = useState<Record<string, boolean>>({});
  const [revealingStates, setRevealingStates] = useState<Record<string, boolean>>({});
  const [revealedValues, setRevealedValues] = useState<Record<string, string>>({});
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  const isAdmin = isAdminUser(user);

  const sortedSkills = useMemo(
    () => [...skills].sort((left, right) => left.name.localeCompare(right.name)),
    [skills],
  );

  const filteredSkills = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return sortedSkills;
    }

    return sortedSkills.filter((skill) => {
      const secretText = skill.secret_statuses
        .map((secret) => `${secret.label} ${secret.key} ${secret.description}`)
        .join(' ')
        .toLowerCase();

      return [skill.name, skill.skill_name, skill.description, secretText]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [searchQuery, sortedSkills]);

  const stats = useMemo(() => {
    const secretCount = skills.reduce((total, skill) => total + skill.secret_statuses.length, 0);
    const configuredCount = skills.reduce(
      (total, skill) =>
        total +
        skill.secret_statuses.filter(
          (secret) =>
            secret.user_value_set || secret.organization_value_set || secret.environment_value_set,
        ).length,
      0,
    );
    const configurableSkillCount = skills.filter((skill) => skill.secret_statuses.length > 0).length;

    return {
      totalSkills: skills.length,
      secretCount,
      configuredCount,
      configurableSkillCount,
    };
  }, [skills]);

  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef<string | null>(null);

  useEffect(() => {
    void loadSkills();
  }, []);

  // Auto-expand a specific skill's secret-configuration section when navigated
  // to with ?selected=<skill_name>. Used by the Monitor setup wizard's "Set up"
  // CTA on Sources marked "Needs setup" — lands the admin directly on the
  // credential inputs (which live inside each skill card's "Configure
  // variables" collapsible), NOT on the file-browser detail modal.
  //
  // After expanding, scrolls the card into view and strips `selected` from the
  // URL so a manual close/refresh doesn't loop.
  useEffect(() => {
    const selected = searchParams.get('selected');
    if (!selected || skills.length === 0) return;
    if (autoOpenedRef.current === selected) return;
    if (!skills.some((s) => s.skill_name === selected)) return;
    autoOpenedRef.current = selected;

    setExpandedSkills((current) => ({ ...current, [selected]: true }));

    // Defer scroll until after the Collapsible has rendered its content so
    // the card's final height is accurate.
    requestAnimationFrame(() => {
      const card = document.getElementById(`skill-card-${selected}`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    // Strip only `selected` from the URL — preserve any other params (filter,
    // tab, return-to references) the page may carry now or in the future.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('selected');
    setSearchParams(nextParams, { replace: true });
  }, [skills, searchParams, setSearchParams]);

  const loadSkills = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth('/v1/skills');
      setSkills(response as SkillSummary[]);
    } catch (fetchError) {
      console.error('Failed to load skills', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const openSkillDetail = async (skillName: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setSelectedFilePath('SKILL.md');
    setSelectedFileContent('');

    try {
      const response = await fetchWithAuth(`/v1/skills/${skillName}`);
      const nextDetail = response as SkillDetail;
      setDetail(nextDetail);
      await loadSkillFile(skillName, 'SKILL.md');
    } catch (fetchError) {
      console.error('Failed to load skill detail', fetchError);
      setDetailError(fetchError instanceof Error ? fetchError.message : 'Failed to load skill detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeSkillDetail = () => {
    setDetail(null);
    setDetailError(null);
    setSelectedFilePath('SKILL.md');
    setSelectedFileContent('');
  };

  const loadSkillFile = async (skillName: string, relativePath: string) => {
    setFileLoading(true);
    try {
      const response = await fetchWithAuth(
        `/v1/skills/${skillName}/files/${encodeURIComponent(relativePath).replace(/%2F/g, '/')}`,
      );
      const fileResponse = response as SkillFileContentResponse;
      setSelectedFilePath(relativePath);
      setSelectedFileContent(fileResponse.content);
    } catch (fetchError) {
      console.error('Failed to load skill file', fetchError);
      setSelectedFilePath(relativePath);
      setSelectedFileContent(
        fetchError instanceof Error ? fetchError.message : 'Failed to load selected file',
      );
    } finally {
      setFileLoading(false);
    }
  };

  const updateDraftValue = (skillName: string, secretKey: string, scope: SecretScope, value: string) => {
    const stateKey = buildSecretStateKey(skillName, secretKey, scope);
    setDraftValues((current) => ({ ...current, [stateKey]: value }));
  };

  const copyValue = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus(key);
      window.setTimeout(() => setCopyStatus(null), 2000);
    } catch (copyError) {
      console.error('Failed to copy secret', copyError);
    }
  };

  const saveSecret = async (skillName: string, secretKey: string, scope: SecretScope) => {
    const stateKey = buildSecretStateKey(skillName, secretKey, scope);
    const value = draftValues[stateKey]?.trim();
    if (!value) return;

    setSavingStates((current) => ({ ...current, [stateKey]: true }));
    setError(null);
    try {
      await fetchWithAuth(`/v1/skills/${skillName}/secrets/${scope}/${secretKey}`, {
        method: 'PUT',
        body: JSON.stringify({ value }),
      });
      setDraftValues((current) => ({ ...current, [stateKey]: '' }));
      await loadSkills();
      if (detail?.skill_name === skillName) {
        await openSkillDetail(skillName);
      }
    } catch (fetchError) {
      console.error('Failed to save secret', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to save secret');
    } finally {
      setSavingStates((current) => ({ ...current, [stateKey]: false }));
    }
  };

  const deleteSecret = async (skillName: string, secretKey: string, scope: SecretScope) => {
    const confirmed = window.confirm(`Delete the ${scope} value for ${secretKey}?`);
    if (!confirmed) return;

    const stateKey = buildSecretStateKey(skillName, secretKey, scope);
    setSavingStates((current) => ({ ...current, [stateKey]: true }));
    setError(null);
    try {
      await fetchWithAuth(`/v1/skills/${skillName}/secrets/${scope}/${secretKey}`, {
        method: 'DELETE',
      });
      setRevealedValues((current) => {
        const next = { ...current };
        delete next[stateKey];
        return next;
      });
      await loadSkills();
      if (detail?.skill_name === skillName) {
        await openSkillDetail(skillName);
      }
    } catch (fetchError) {
      console.error('Failed to delete secret', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to delete secret');
    } finally {
      setSavingStates((current) => ({ ...current, [stateKey]: false }));
    }
  };

  const revealSecret = async (skillName: string, secretKey: string, scope: SecretScope) => {
    const stateKey = buildSecretStateKey(skillName, secretKey, scope);
    setRevealingStates((current) => ({ ...current, [stateKey]: true }));
    setError(null);
    try {
      const response = await fetchWithAuth(`/v1/skills/${skillName}/secrets/${scope}/${secretKey}/reveal`);
      setRevealedValues((current) => ({ ...current, [stateKey]: (response as { value: string }).value }));
    } catch (fetchError) {
      console.error('Failed to reveal secret', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to reveal secret');
    } finally {
      setRevealingStates((current) => ({ ...current, [stateKey]: false }));
    }
  };

  const renderSourceBadge = (effectiveSource: SkillSecretStatus['effective_source']) => (
    <Badge variant="outline" className={sourceBadgeStyles[effectiveSource]}>
      {sourceLabels[effectiveSource]}
    </Badge>
  );

  const toggleSkillExpanded = (skillName: string, open: boolean) => {
    setExpandedSkills((current) => ({ ...current, [skillName]: open }));
  };

  const renderSecretForm = (skillName: string, secret: SkillSecretStatus, scope: SecretScope) => {
    if (!secret.scopes.includes(scope)) {
      return null;
    }

    if (scope === 'organization' && !isAdmin) {
      return null;
    }

    const stateKey = buildSecretStateKey(skillName, secret.key, scope);
    const valueIsSet = scope === 'user' ? secret.user_value_set : secret.organization_value_set;
    const revealedValue = revealedValues[stateKey];
    const isSaving = savingStates[stateKey] ?? false;
    const isRevealing = revealingStates[stateKey] ?? false;

    // Warn the admin when they've saved a personal override but no org
    // default for a required secret — scheduled monitors will silently
    // fail because they read the org default, not the user override.
    const showMonitorMisconfigWarning =
      scope === 'organization'
        && secret.required
        && !secret.organization_value_set
        && secret.user_value_set;

    return (
      <Card
        key={stateKey}
        variant="outline"
        padding="sm"
        className="gap-4 border-slate-200 bg-slate-50/70 shadow-none"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <Heading level={5} className="text-slate-900">
              {scopeCopy[scope].title}
            </Heading>
            <p className="text-xs text-slate-500">
              {scopeCopy[scope].description}
            </p>
            <p className="pt-1 text-xs font-medium text-slate-600">
              {valueIsSet ? 'Value configured' : 'No value configured'}
            </p>
          </div>
          {valueIsSet && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void revealSecret(skillName, secret.key, scope)}
                disabled={isRevealing}
                leftIcon={<IconWrapper icon={FiEye} />}
              >
                {isRevealing ? 'Loading...' : 'Reveal'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void deleteSecret(skillName, secret.key, scope)}
                disabled={isSaving}
                leftIcon={<IconWrapper icon={FiTrash2} />}
                className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-700"
              >
                Delete
              </Button>
            </div>
          )}
        </div>

        {showMonitorMisconfigWarning && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-medium text-amber-900">
              ⚠ A personal override is configured for this secret, but no
              Organization default is set.
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Scheduled monitors and imports will fail — they only use the
              Organization default. Save the value here too.
            </p>
          </div>
        )}

        {revealedValue && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-emerald-700">
                  Revealed value
                </p>
                <code className="block whitespace-pre-wrap break-all text-sm text-emerald-950">
                  {revealedValue}
                </code>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void copyValue(revealedValue, stateKey)}
                leftIcon={<IconWrapper icon={copyStatus === stateKey ? FiEyeOff : FiKey} />}
                className="border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-100"
              >
                {copyStatus === stateKey ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            type={secret.sensitive ? 'password' : 'text'}
            value={draftValues[stateKey] ?? ''}
            onChange={(event) => updateDraftValue(skillName, secret.key, scope, event.target.value)}
            placeholder={valueIsSet ? 'Replace value' : 'Enter value'}
            className="min-w-0 flex-1 bg-white"
          />
          <Button
            onClick={() => void saveSecret(skillName, secret.key, scope)}
            disabled={isSaving || !(draftValues[stateKey] ?? '').trim()}
            loading={isSaving}
            className="shrink-0 whitespace-nowrap"
          >
            {scopeCopy[scope].action}
          </Button>
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-auto bg-slate-50">
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-56 rounded bg-slate-200" />
            <div className="grid gap-4 md:grid-cols-3">
              <div className="h-28 rounded-xl bg-slate-100" />
              <div className="h-28 rounded-xl bg-slate-100" />
              <div className="h-28 rounded-xl bg-slate-100" />
            </div>
            <div className="h-56 rounded-xl bg-slate-100" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-slate-50">
      <div className="p-8">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <Heading level={2} className="tracking-tight text-gray-900">
              Skills
            </Heading>
            <p className="max-w-3xl text-sm text-gray-600">
              Review the portable skill catalog, inspect the packaged bundle, and configure the
              declared environment variables each skill needs. Personal values override
              organization defaults, and session-only overrides still take precedence at runtime.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => void loadSkills()}
            leftIcon={<IconWrapper icon={FiRefreshCw} />}
          >
            Refresh
          </Button>
        </div>

        <div className="mb-6 flex flex-col gap-3">
          <div className="space-y-1">
            <Heading level={4}>Find a skill</Heading>
            <p className="text-sm text-slate-500">
              Search by skill name, description, or declared variable.
            </p>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
            <div className="relative group flex-1">
              <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-primary/20 via-purple/10 to-teal/20 blur opacity-0 transition-opacity duration-300 group-focus-within:opacity-100" />
              <div className="relative">
                <div
                  className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
                    searchQuery ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <IconWrapper icon={FiSearch} className="h-5 w-5" />
                </div>

                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search skills by name, variable, or description"
                  // type=search tells Chrome this is a non-credential field
                  // so it stops aggressively autofilling saved emails into it.
                  // Chrome ignores autoComplete="off" on plain text inputs it
                  // suspects could be login-related; type=search opts out of
                  // that heuristic and respects the off attribute.
                  type="search"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  // `data-form-type=other` is a Chrome-specific signal that
                  // also suppresses the email-autofill heuristic.
                  data-form-type="other"
                  name="skill-filter"
                  className="h-14 rounded-xl border-border/60 bg-card pl-12 pr-12 text-base shadow-sm placeholder:text-muted-foreground/60 focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
                />

                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                    aria-label="Clear skill search"
                  >
                    <IconWrapper icon={FiX} className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 self-end lg:self-center">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-muted/50 px-3 py-1">
                <IconWrapper icon={FiSearch} className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground/80">
                  {filteredSkills.length} of {sortedSkills.length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <Card
            variant="outline"
            padding="sm"
            className="mb-6 border-rose-200 bg-rose-50 text-sm text-rose-700 shadow-none"
          >
            {error}
          </Card>
        )}

        <div className="mb-8 grid gap-4 md:grid-cols-3 xl:grid-cols-4">
          <Card className={statCardClassName}>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500">Portable skills</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{stats.totalSkills}</p>
            </CardContent>
          </Card>
          <Card className={statCardClassName}>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500">Skills with configuration</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{stats.configurableSkillCount}</p>
            </CardContent>
          </Card>
          <Card className={statCardClassName}>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500">Declared variables</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{stats.secretCount}</p>
            </CardContent>
          </Card>
          <Card className={statCardClassName}>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500">Configured values</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{stats.configuredCount}</p>
            </CardContent>
          </Card>
        </div>

        <div className="columns-1 gap-6 xl:columns-2">
          {filteredSkills.map((skill) => (
            <Card
              key={skill.skill_name}
              id={`skill-card-${skill.skill_name}`}
              className="mb-6 break-inside-avoid gap-0 overflow-hidden border-slate-200/80"
            >
              <CardHeader className="border-b bg-white">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle>{skill.name}</CardTitle>
                    <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                      {skill.version || 'unknown version'}
                    </Badge>
                    {skill.secret_statuses.length > 0 && (
                      <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                        {skill.secret_statuses.length} variable{skill.secret_statuses.length === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="max-w-2xl">
                    {skill.description || 'No description provided.'}
                  </CardDescription>
                </div>
                <CardAction className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void openSkillDetail(skill.skill_name)}
                    leftIcon={<IconWrapper icon={FiEye} />}
                  >
                    View skill
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void downloadFileWithAuth(`/v1/skills/${skill.skill_name}/download`, `${skill.skill_name}.zip`)
                    }
                    leftIcon={<IconWrapper icon={FiDownload} />}
                  >
                    Download ZIP
                  </Button>
                </CardAction>
              </CardHeader>

              <CardContent className="space-y-5 bg-slate-50/60 py-6">
                {skill.secret_statuses.length === 0 ? (
                  <Card
                    variant="outline"
                    padding="sm"
                    className="border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500 shadow-none"
                  >
                    This skill does not declare any configurable environment variables.
                  </Card>
                ) : (
                  <Collapsible
                    open={!!expandedSkills[skill.skill_name]}
                    onOpenChange={(open) => toggleSkillExpanded(skill.skill_name, open)}
                  >
                    <Card
                      variant="outline"
                      padding="sm"
                      className="gap-4 border-slate-200 bg-white shadow-none"
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-2">
                          <Heading level={4}>Configuration</Heading>
                          <p className="text-sm text-slate-600">
                            {skill.secret_statuses.length} declared variable
                            {skill.secret_statuses.length === 1 ? '' : 's'}.
                            {' '}
                            {
                              skill.secret_statuses.filter(
                                (secret) =>
                                  secret.user_value_set ||
                                  secret.organization_value_set ||
                                  secret.environment_value_set,
                              ).length
                            }
                            {' '}
                            currently configured or inherited.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {skill.secret_statuses.some((secret) => secret.required) && (
                              <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
                                Has required variables
                              </Badge>
                            )}
                            {skill.secret_statuses.some(
                              (secret) => secret.effective_source === 'organization',
                            ) && (
                              <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                                Org defaults configured
                              </Badge>
                            )}
                            {skill.secret_statuses.some((secret) => secret.effective_source === 'user') && (
                              <Badge
                                variant="outline"
                                className="border-emerald-200 bg-emerald-50 text-emerald-700"
                              >
                                Personal overrides configured
                              </Badge>
                            )}
                          </div>
                        </div>
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            rightIcon={
                              <IconWrapper
                                icon={FiChevronDown}
                                className={`transition-transform ${
                                  expandedSkills[skill.skill_name] ? 'rotate-180' : ''
                                }`}
                              />
                            }
                          >
                            {expandedSkills[skill.skill_name] ? 'Hide variables' : 'Configure variables'}
                          </Button>
                        </CollapsibleTrigger>
                      </div>

                      <CollapsibleContent className="space-y-5 pt-2">
                        {skill.secret_statuses.map((secret) => (
                          <Card
                            key={`${skill.skill_name}:${secret.key}`}
                            variant="outline"
                            padding="sm"
                            className="gap-4 border-slate-200 bg-white shadow-none"
                          >
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Heading level={4}>{secret.label}</Heading>
                                  <Badge variant="outline" className="border-slate-200 bg-slate-50 font-mono text-slate-600">
                                    {secret.key}
                                  </Badge>
                                  {secret.required && (
                                    <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
                                      Required
                                    </Badge>
                                  )}
                                  {secret.sensitive && (
                                    <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">
                                      Sensitive
                                    </Badge>
                                  )}
                                </div>
                                {secret.description && (
                                  <p className="text-sm text-slate-600">{secret.description}</p>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {renderSourceBadge(secret.effective_source)}
                                {secret.environment_value_set && (
                                  <Badge
                                    variant="outline"
                                    className="border-amber-200 bg-amber-50 text-amber-700"
                                  >
                                    Env available
                                  </Badge>
                                )}
                              </div>
                            </div>

                            <div className="grid gap-4 xl:grid-cols-2">
                              {renderSecretForm(skill.skill_name, secret, 'user')}
                              {renderSecretForm(skill.skill_name, secret, 'organization')}
                            </div>
                          </Card>
                        ))}
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredSkills.length === 0 && (
          <Card
            variant="outline"
            padding="sm"
            className="border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500 shadow-none"
          >
            No skills match “{searchQuery.trim()}”.
          </Card>
        )}
      </div>

      {detailLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/25 px-4">
          <Card className="w-full max-w-sm">
            <CardContent className="p-6 text-sm text-slate-700">Loading skill details...</CardContent>
          </Card>
        </div>
      )}

      {(detail || detailError) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 py-8">
          <Card className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden p-0 shadow-2xl">
            <CardHeader className="border-b bg-white">
              <div className="space-y-2">
                <CardTitle>{detail?.name || 'Skill details'}</CardTitle>
                {detail && <CardDescription>{detail.description}</CardDescription>}
              </div>
              <CardAction>
                <Button variant="outline" onClick={closeSkillDetail}>
                  Close
                </Button>
              </CardAction>
            </CardHeader>

            {detailError ? (
              <CardContent className="p-6 text-sm text-rose-700">{detailError}</CardContent>
            ) : detail ? (
              <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)]">
                <aside className="border-r bg-slate-50 p-4">
                  <Card variant="outline" padding="sm" className="mb-4 gap-4 border-slate-200 bg-white shadow-none">
                    <div className="flex items-center gap-2 text-slate-900">
                      <IconWrapper icon={FiShield} className="text-slate-500" />
                      <Heading level={5}>Bundle overview</Heading>
                    </div>
                    <dl className="space-y-2 text-sm text-slate-600">
                      <div className="flex items-center justify-between gap-3">
                        <dt>Version</dt>
                        <dd className="font-medium text-slate-900">{detail.version}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt>Scripts</dt>
                        <dd className="font-medium text-slate-900">{detail.scripts.length}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt>References</dt>
                        <dd className="font-medium text-slate-900">{detail.references.length}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt>Declared secrets</dt>
                        <dd className="font-medium text-slate-900">{detail.secret_statuses.length}</dd>
                      </div>
                    </dl>
                    <Button
                      onClick={() =>
                        void downloadFileWithAuth(`/v1/skills/${detail.skill_name}/download`, `${detail.skill_name}.zip`)
                      }
                      leftIcon={<IconWrapper icon={FiDownload} />}
                    >
                      Download ZIP
                    </Button>
                  </Card>

                  <Card variant="outline" padding="sm" className="gap-3 border-slate-200 bg-white shadow-none">
                    <div className="px-1">
                      <Heading level={5}>Files</Heading>
                      <p className="mt-1 text-xs text-slate-500">Browse the packaged skill bundle.</p>
                    </div>
                    <div className="max-h-[55vh] space-y-1 overflow-y-auto">
                      <Button
                        variant={selectedFilePath === 'SKILL.md' ? 'default' : 'ghost'}
                        className="h-auto w-full justify-start px-3 py-2 text-left"
                        onClick={() => void loadSkillFile(detail.skill_name, 'SKILL.md')}
                      >
                        <div>
                          <div className="font-medium">SKILL.md</div>
                        </div>
                      </Button>
                      {detail.files.map((file) => (
                        <Button
                          key={file.relative_path}
                          variant={selectedFilePath === file.relative_path ? 'default' : 'ghost'}
                          className="h-auto w-full justify-start px-3 py-2 text-left"
                          onClick={() => void loadSkillFile(detail.skill_name, file.relative_path)}
                          disabled={!file.viewable}
                        >
                          <div>
                            <div className="font-medium">{file.relative_path}</div>
                            <div
                              className={`text-xs ${
                                selectedFilePath === file.relative_path ? 'text-slate-200' : 'text-slate-500'
                              }`}
                            >
                              {file.category} {file.viewable ? '' : '• binary/non-viewable'}
                            </div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </Card>
                </aside>

                <main className="min-h-0 overflow-y-auto bg-white">
                  <div className="border-b px-6 py-4">
                    <Heading level={5}>{selectedFilePath}</Heading>
                  </div>
                  <div className="px-6 py-5">
                    {fileLoading ? (
                      <p className="text-sm text-slate-500">Loading file...</p>
                    ) : (
                      <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-5 text-sm text-slate-100">
                        {selectedFileContent}
                      </pre>
                    )}
                  </div>
                </main>
              </div>
            ) : null}
            <CardFooter className="hidden" />
          </Card>
        </div>
      )}
    </div>
  );
};

export default SkillsView;
